import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import HeroSection from "./components/HeroSection";
import HowItWorks from "./components/HowItWorks";
import MagazineShowcase from "./components/MagazineShowcase";
import DistributionOptions from "./components/DistributionOptions";
import CTASection from "./components/CTASection";
import ApplyCreatorBanner from "./components/ApplyCreatorBanner";
import FAQSection from "./components/FAQSection";
import SEOHead from "../../components/feature/SEOHead";
import JsonLd from "../../components/feature/JsonLd";

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? "";

const homeSchema: Record<string, unknown>[] = [
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "The Magazine Co.",
    url: siteUrl,
    description:
      "The Magazine Co. transforms creator content into beautifully crafted physical magazines, distributed weekly, monthly, or quarterly to subscribers worldwide.",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "The Magazine Co.",
    url: siteUrl,
    foundingDate: "2019",
    description:
      "We turn creator content into premium physical magazines. Zero upfront cost, global distribution, revenue share model.",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+16786633900",
      contactType: "customer support",
      email: "makeitamagazine@gmail.com",
      availableLanguage: "English",
      hoursAvailable: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "16:00",
      },
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Creator Print Magazine Publishing",
    provider: {
      "@type": "Organization",
      name: "The Magazine Co.",
      url: siteUrl,
    },
    description:
      "Turn your digital content into a beautifully designed physical magazine. The Magazine Co. handles editorial design, premium printing, and worldwide distribution — with zero upfront cost and a revenue share model.",
    serviceType: "Print Magazine Publishing",
    areaServed: "Worldwide",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      description: "Zero upfront cost — revenue share model. We only make money when you do.",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Do I need a huge audience to apply?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Nope! Seriously, no follower minimums here. We've worked with creators who had 300 subscribers and creators who had 300,000. What we actually care about is that your audience is engaged and that you show up consistently. Quality over quantity, every time.",
        },
      },
      {
        "@type": "Question",
        name: "What does it actually cost me?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Zero upfront. Not a dollar. We cover design, print production, and distribution — and we recoup that through a revenue share on magazine sales. If your magazine doesn't sell, we don't make money either.",
        },
      },
      {
        "@type": "Question",
        name: "What kind of content works in print?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Way more than you'd think. Written essays, photography, illustration, mixed media, interviews, data journalism, travel diaries, recipes, fitness guides — if it's good content, it works in print.",
        },
      },
      {
        "@type": "Question",
        name: "How long until my first issue is actually out?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Once you're approved, we typically get your first issue designed, printed, and shipped within 4 to 6 weeks.",
        },
      },
      {
        "@type": "Question",
        name: "Do I have to stop posting online?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Absolutely not — keep doing everything you're already doing. The print magazine is additive, not a replacement. Most of our creators actually find their digital audience grows after launching a magazine.",
        },
      },
      {
        "@type": "Question",
        name: "Can I choose how often my magazine comes out?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes! Weekly, monthly, or quarterly — you pick the cadence that fits your output and your life.",
        },
      },
      {
        "@type": "Question",
        name: "What if I'm not sure my content will look good in print?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "That's literally what our design team is here for. We take your content and make it look stunning on the page — you don't need to be a designer or know anything about print layout.",
        },
      },
      {
        "@type": "Question",
        name: "Is this only for certain niches or topics?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No gatekeeping here. Lifestyle, fitness, food, finance, travel, parenting, art, sports, tech, sustainability — we've seen it all and we love the variety.",
        },
      },
    ],
  },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="The Magazine Co. | Turn Your Content Into a Premium Print Magazine"
        description="The Magazine Co. transforms creator content into beautifully crafted physical magazines, distributed weekly, monthly, or quarterly to subscribers worldwide."
        keywords="creator magazine, print magazine, content to print, independent magazine publishing, self-publishing"
        path="/"
      />
      <JsonLd id="home-schema" schema={homeSchema} />
      <Navbar />
      <main>
        <HeroSection />
        <HowItWorks />
        <MagazineShowcase />
        <DistributionOptions />
        <ApplyCreatorBanner />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}
